import { Link } from '@tanstack/react-router';
import { Badge } from '@yhelp/ui';
import type { Ticket, TicketClassification } from '@yhelp/shared-types';
import { ClassificationBadge } from '@/components/molecules/ClassificationBadge';
import { statusLabel } from '@/lib/ticket-status';
import { productLabel } from '@/lib/ysec-catalog';

function formatUpdated(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function TicketRowActions({
  ticketId,
  onAssume,
  showAssume,
  assuming,
}: {
  ticketId: string;
  onAssume?: (id: string) => void;
  showAssume?: boolean;
  assuming?: boolean;
}) {
  if (!showAssume || !onAssume) return null;
  return (
    <button
      type="button"
      disabled={assuming}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onAssume(ticketId);
      }}
      className="rounded-md bg-yhelp-yellow px-3 py-1 text-xs font-semibold text-yhelp-black transition-opacity hover:opacity-90 disabled:opacity-50"
    >
      {assuming ? '…' : 'Assumir'}
    </button>
  );
}

export function TicketListTable({
  tickets,
  showAssume,
  onAssume,
  assumingId,
  compact,
}: {
  tickets: Ticket[];
  showAssume?: boolean;
  onAssume?: (id: string) => void;
  assumingId?: string | null;
  compact?: boolean;
}) {
  return (
    <>
      {/* Desktop table */}
      <div className="surface-card hidden overflow-hidden md:block" data-tour="tickets-table">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-100 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
            <tr>
              <th className="px-4 py-3 w-20" scope="col">
                Nº
              </th>
              <th className="px-4 py-3" scope="col">
                Título
              </th>
              {!compact && (
                <>
                  <th className="px-4 py-3 w-28" scope="col">
                    Status
                  </th>
                  <th className="px-4 py-3 w-24" scope="col">
                    Prioridade
                  </th>
                </>
              )}
              <th className="px-4 py-3 w-28" scope="col">
                Produto
              </th>
              <th className="px-4 py-3 w-32" scope="col">
                Classificação
              </th>
              <th className="px-4 py-3 w-28" scope="col">
                Atualizado
              </th>
              {showAssume ? (
                <th className="px-4 py-3 w-24" scope="col">
                  <span className="sr-only">Ações</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {tickets.map((t, i) => (
              <tr
                key={t.id}
                data-tour={i === 0 ? 'ticket-row-first' : undefined}
                className="list-stagger transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/40"
              >
                <td className="px-4 py-3 font-mono text-muted">#{t.number}</td>
                <td className="px-4 py-3">
                  <Link
                    to="/tickets/$ticketId"
                    params={{ ticketId: t.id }}
                    className="font-medium text-yhelp-black hover:underline dark:text-white"
                  >
                    {t.title}
                  </Link>
                </td>
                {!compact && (
                  <>
                    <td className="px-4 py-3">
                      <Badge variant="status" status={t.status}>
                        {statusLabel(t.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="priority" priority={t.priority}>
                        {t.priority}
                      </Badge>
                    </td>
                  </>
                )}
                <td className="px-4 py-3">
                  {t.product ? (
                    <span className="inline-flex rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                      {productLabel(t.product)}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {t.classification ? (
                    <ClassificationBadge
                      classification={t.classification as TicketClassification}
                    />
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted">{formatUpdated(t.updated_at)}</td>
                {showAssume ? (
                  <td className="px-4 py-3">
                    <TicketRowActions
                      ticketId={t.id}
                      onAssume={onAssume}
                      showAssume={showAssume}
                      assuming={assumingId === t.id}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="divide-y divide-neutral-200 surface-card overflow-hidden md:hidden dark:divide-neutral-800">
        {tickets.map((t, i) => (
          <li
            key={t.id}
            data-tour={i === 0 ? 'ticket-row-first' : undefined}
            className="list-stagger px-4 py-3"
          >
            <Link
              to="/tickets/$ticketId"
              params={{ ticketId: t.id }}
              className="flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-sm text-muted">#{t.number}</span>
                <Badge variant="status" status={t.status}>
                  {statusLabel(t.status)}
                </Badge>
              </div>
              <span className="font-medium text-yhelp-black dark:text-white">{t.title}</span>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="priority" priority={t.priority}>
                  {t.priority}
                </Badge>
                {t.product ? (
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium dark:bg-neutral-800">
                    {productLabel(t.product)}
                  </span>
                ) : null}
                {t.classification ? (
                  <ClassificationBadge classification={t.classification as TicketClassification} />
                ) : null}
              </div>
            </Link>
            {showAssume && onAssume ? (
              <div className="mt-2">
                <TicketRowActions
                  ticketId={t.id}
                  onAssume={onAssume}
                  showAssume
                  assuming={assumingId === t.id}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}
